import { useEffect, useState } from "react";
import { Spinner } from "@/components/ui/spinner";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Save } from "lucide-react";
import { useAuth, useUser } from "@clerk/react";
import { useOrganizationSettingsStore } from "@/stores";
import { toast } from "sonner";
import {
  Frame,
  FrameHeader,
  FrameTitle,
  FrameDescription,
  FramePanel,
  FrameFooter,
} from "@/components/ui/frame";

export function DataSection() {
  const { organizationInfo, saveOrganizationSetting, isLoading } =
    useOrganizationSettingsStore();
  const { orgId, getToken } = useAuth();
  const { user } = useUser();

  const [extractedInfo, setExtractedInfo] = useState("");
  const [initialExtractedInfo, setInitialExtractedInfo] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const value = organizationInfo?.extracted_information || "";
    setExtractedInfo(value);
    setInitialExtractedInfo(value);
  }, [organizationInfo]);

  const hasChanges = extractedInfo !== initialExtractedInfo;

  const handleSave = async () => {
    if (!orgId || !user?.id) return;
    setSaving(true);
    try {
      const token = await getToken();
      if (!token) return;
      const success = await saveOrganizationSetting(
        orgId,
        user.id,
        "extracted_information",
        extractedInfo,
        token,
      );
      if (success) {
        setInitialExtractedInfo(extractedInfo);
        toast.success("Extracted Information saved successfully");
      } else {
        toast.error("Failed to save Extracted Information");
      }
    } catch {
      toast.error("Failed to save Extracted Information");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-8">
      <Frame>
        <FrameHeader>
          <FrameTitle>Extracted Information</FrameTitle>
          <FrameDescription>What Amy learned from handoffs.</FrameDescription>
        </FrameHeader>
        {isLoading && !extractedInfo ? (
          <FramePanel>
            <Skeleton className="h-[300px]" />
          </FramePanel>
        ) : (
          <Textarea
            variant="panel"
            className="min-h-[300px]"
            placeholder="Enter extracted information here..."
            value={extractedInfo}
            onChange={(e) => setExtractedInfo(e.target.value)}
            onBlur={handleSave}
          />
        )}
        {hasChanges && (
          <FrameFooter className="flex justify-end">
            <Button
              onClick={handleSave}
              disabled={saving}
              size="sm"
              className="shrink-0 animate-in fade-in-0 slide-in-from-left-2 duration-200"
            >
              {saving ? (
                <Spinner className="size-4" />
              ) : (
                <Save className="size-4" />
              )}
              Save
            </Button>
          </FrameFooter>
        )}
      </Frame>
    </div>
  );
}
